import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, Collapse, IconButton, Tooltip, useTheme,
  Tabs, Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon, ExpandMore, ExpandLess,
  CheckCircle, Warning, Error as ErrorIcon,
  TrendingUp, AccountBalance, Speed, MonetizationOn,
  Assessment, Shield, WaterDrop,
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Print as PrintIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface FinancialRatio {
  ratioId: string; category: string; categoryAr: string;
  ratioName: string; ratioNameAr: string; formula: string; formulaAr: string;
  value: number; displayValue: string; benchmark: string; status: string;
  article: string; articleAr: string;
  numerator?: number; denominator?: number;
}

interface ZScoreResult {
  zScore: number; zone: string; zoneAr: string;
  interpretation: string; interpretationAr: string;
  x1_WorkingCapitalToAssets: number; x2_RetainedEarningsToAssets: number;
  x3_EBITToAssets: number; x4_EquityToDebt: number; x5_RevenueToAssets: number;
}

interface RiskIndicator {
  riskName: string; riskNameAr: string; severity: string;
  description: string; descriptionAr: string;
  recommendation: string; recommendationAr: string;
}

interface AnalysisResult {
  generatedAt: string; fromDate?: string; toDate?: string;
  overallStatus: string; overallStatusAr: string;
  totalRatios: number; goodCount: number; warningCount: number; dangerCount: number;
  totalAssets: number; totalCurrentAssets: number; totalFixedAssets: number;
  totalLiabilities: number; totalCurrentLiabilities: number; totalLongTermLiabilities: number;
  totalEquity: number; totalRevenue: number; grossProfit: number;
  operatingIncome: number; netIncome: number; cogs: number; operatingExpenses: number;
  retainedEarnings: number; cashFromOperations: number; cashFromInvesting: number; cashFromFinancing: number;
  liquidityRatios: FinancialRatio[]; profitabilityRatios: FinancialRatio[];
  solvencyRatios: FinancialRatio[]; efficiencyRatios: FinancialRatio[];
  cashFlowRatios: FinancialRatio[];
  zScore: ZScoreResult; risks: RiskIndicator[];
}

const FinancialAnalysisReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { minDate, maxDate, startDateStr, endDateStr } = useFiscalDateRange();

  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [fromDate, setFromDate] = useState<Date | null>(minDate);
  const [toDate, setToDate] = useState<Date | null>(maxDate);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => { companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {}); }, []);

  const fmtDate = (d: Date | null) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromDate', fmtDate(fromDate));
      if (toDate) params.append('toDate', fmtDate(toDate));
      const { data } = await axiosInstance.get(`/FinancialAnalysis?${params.toString()}`);
      setResult(data);
      toast.success(isRTL ? 'تم التحليل بنجاح' : 'Analysis completed');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const statusColor = (s: string) => s === 'Good' ? '#4caf50' : s === 'Warning' ? '#ff9800' : '#f44336';
  const statusIcon = (s: string) => s === 'Good' ? <CheckCircle sx={{ color: '#4caf50' }} /> : s === 'Warning' ? <Warning sx={{ color: '#ff9800' }} /> : <ErrorIcon sx={{ color: '#f44336' }} />;
  const severityColor = (s: string) => s === 'Low' ? 'success' : s === 'Medium' ? 'warning' : s === 'High' ? 'error' : 'error';

  const fmt = (n: number) => n.toFixed(3);

  const overallGradient = (s: string) => s === 'Healthy' ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : s === 'Caution' ? 'linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)' : 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)';

  const zScoreGradient = (zone: string) => zone === 'Safe' ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : zone === 'Grey' ? 'linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)' : 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)';

  const categoryIcon = (cat: string) => {
    switch (cat) {
      case 'Liquidity': return <WaterDrop />;
      case 'Profitability': return <TrendingUp />;
      case 'Solvency': return <Shield />;
      case 'Efficiency': return <Speed />;
      case 'Cash Flow': return <MonetizationOn />;
      default: return <Assessment />;
    }
  };

  const reportTitle = isRTL ? 'التحليل المالي الشامل' : 'Comprehensive Financial Analysis';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;

  const tabNames = [
    { en: 'Liquidity', ar: 'السيولة' }, { en: 'Profitability', ar: 'الربحية' },
    { en: 'Solvency', ar: 'الملاءة' }, { en: 'Efficiency', ar: 'الكفاءة' },
    { en: 'Cash Flow', ar: 'التدفقات النقدية' }, { en: 'Raw Data', ar: 'البيانات الخام' },
  ];

  const getActiveRatios = (): FinancialRatio[] => {
    if (!result) return [];
    if (tab === 0) return result.liquidityRatios;
    if (tab === 1) return result.profitabilityRatios;
    if (tab === 2) return result.solvencyRatios;
    if (tab === 3) return result.efficiencyRatios;
    if (tab === 4) return result.cashFlowRatios;
    return [...result.liquidityRatios, ...result.profitabilityRatios, ...result.solvencyRatios, ...result.efficiencyRatios, ...result.cashFlowRatios];
  };

  const getActiveTabName = () => isRTL ? tabNames[tab]?.ar || '' : tabNames[tab]?.en || '';

  const exportToExcel = async () => {
    if (!result) return;
    try {
      const wb = new ExcelJS.Workbook(); const ws = wb.addWorksheet(reportTitle);
      if (companyInfo?.logoBase64) { try { const img = wb.addImage({ base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64, extension: 'png' }); ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } }); } catch {} }
      ws.mergeCells('C1:G1'); const cc = ws.getCell('C1'); cc.value = compName || ''; cc.font = { size: 18, bold: true, color: { argb: 'FF667eea' } }; cc.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.mergeCells('A3:G3'); const tc = ws.getCell('A3'); tc.value = reportTitle; tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; tc.alignment = { horizontal: 'center' }; tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } };
      ws.mergeCells('A4:G4'); ws.getCell('A4').value = `${getActiveTabName()} | Z-Score: ${result.zScore.zScore.toFixed(2)} (${isRTL ? result.zScore.zoneAr : result.zScore.zone}) | ${isRTL ? result.overallStatusAr : result.overallStatus}`; ws.getCell('A4').alignment = { horizontal: 'center' };
      const headers = [isRTL ? 'الفئة' : 'Category', isRTL ? 'النسبة' : 'Ratio', isRTL ? 'المعادلة' : 'Formula', isRTL ? 'القيمة' : 'Value', isRTL ? 'المعيار' : 'Benchmark', isRTL ? 'الحالة' : 'Status', isRTL ? 'الرأي' : 'Verdict'];
      const hr = ws.getRow(6); headers.forEach((h, i) => { const c = hr.getCell(i + 1); c.value = h; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } }; c.alignment = { horizontal: 'center' }; });
      getActiveRatios().forEach((r, i) => { const row = ws.getRow(7 + i); row.getCell(1).value = isRTL ? r.categoryAr : r.category; row.getCell(2).value = isRTL ? r.ratioNameAr : r.ratioName; row.getCell(3).value = isRTL ? r.formulaAr : r.formula; row.getCell(4).value = r.displayValue; row.getCell(5).value = r.benchmark; row.getCell(6).value = r.status;
        row.getCell(7).value = r.status === 'Good' ? (isRTL ? 'آمن' : 'Safe') : r.status === 'Warning' ? (isRTL ? 'مراقبة' : 'Watch') : (isRTL ? 'خطر' : 'Risk');
        const bg = r.status === 'Good' ? 'FFE8F5E9' : r.status === 'Warning' ? 'FFFFF3E0' : 'FFFFEBEE';
        row.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }; }); });
      ws.columns = [{ width: 18 }, { width: 30 }, { width: 35 }, { width: 15 }, { width: 12 }, { width: 10 }, { width: 12 }];
      const buf = await wb.xlsx.writeBuffer(); const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `FinancialAnalysis_${Date.now()}.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  const exportToPdf = async () => {
    if (!result) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' }); const pw = doc.internal.pageSize.width;
      doc.setFillColor(245, 240, 250); doc.rect(0, 0, pw, 45, 'F');
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(102, 126, 234); doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(102, 126, 234); doc.roundedRect(pw / 2 - 55, 32, 110, 10, 3, 3, 'F');
      doc.setFontSize(12); doc.setTextColor(255, 255, 255); doc.text(reportTitle, pw / 2, 38.5, { align: 'center' });
      doc.setFontSize(9); doc.setTextColor(80, 80, 80); doc.text(`Z-Score: ${result.zScore.zScore.toFixed(2)} (${isRTL ? result.zScore.zoneAr : result.zScore.zone})`, pw / 2, 48, { align: 'center' });
      const ratios = getActiveRatios();
      autoTable(doc, { head: [[isRTL ? 'الفئة' : 'Category', isRTL ? 'النسبة' : 'Ratio', isRTL ? 'المعادلة' : 'Formula', isRTL ? 'القيمة' : 'Value', isRTL ? 'المعيار' : 'Benchmark', isRTL ? 'الحالة' : 'Status']],
        body: ratios.map(r => [isRTL ? r.categoryAr : r.category, isRTL ? r.ratioNameAr : r.ratioName, isRTL ? r.formulaAr : r.formula, r.displayValue, r.benchmark, r.status === 'Good' ? (isRTL ? 'آمن' : 'Safe') : r.status === 'Warning' ? (isRTL ? 'مراقبة' : 'Watch') : (isRTL ? 'خطر' : 'Risk')]),
        startY: 52, theme: 'grid', headStyles: { fillColor: [102, 126, 234], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 7, textColor: [50, 50, 50] },
        didParseCell: (data: any) => { if (data.section === 'body') { const s = ratios[data.row.index]?.status; if (s === 'Good') data.cell.styles.fillColor = [232, 245, 233]; else if (s === 'Warning') data.cell.styles.fillColor = [255, 243, 224]; else data.cell.styles.fillColor = [255, 235, 238]; } },
      });
      doc.save(`FinancialAnalysis_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  const handlePrint = () => {
    if (!result) return;
    const ratios = getActiveRatios();
    const w = window.open('', '_blank', 'width=1000,height=700'); if (!w) return;
    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:20px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #667eea;padding-bottom:10px;margin-bottom:10px}
      .header img{max-height:60px} .header h1{color:#667eea;margin:0;font-size:22px} .header .sub{color:#666;font-size:12px}
      .zscore{text-align:center;background:${result.zScore.zone === 'Safe' ? '#e8f5e9' : result.zScore.zone === 'Grey' ? '#fff3e0' : '#ffebee'};padding:12px;border-radius:8px;margin-bottom:12px;font-size:18px;font-weight:bold}
      table{width:100%;border-collapse:collapse;font-size:10px} th{background:#667eea;color:white;padding:6px;text-align:${isRTL ? 'right' : 'left'}}
      td{padding:5px 6px;border:1px solid #ddd} .Good{background:#e8f5e9} .Warning{background:#fff3e0} .Danger{background:#ffebee}
      @media print{.zscore{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
    <div class="header"><div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
    <div style="text-align:center;flex:1"><h1>${reportTitle}</h1><div class="sub">${compName || ''} | ${companyInfo?.address || ''} | ${companyInfo?.phone || ''}</div></div><div style="width:80px"></div></div>
    <div class="zscore">Altman Z-Score: ${result.zScore.zScore.toFixed(2)} — ${isRTL ? result.zScore.zoneAr : result.zScore.zone} | ${isRTL ? result.overallStatusAr : result.overallStatus}</div>
    <table><thead><tr><th>${isRTL ? 'الفئة' : 'Category'}</th><th>${isRTL ? 'النسبة' : 'Ratio'}</th><th>${isRTL ? 'المعادلة' : 'Formula'}</th><th>${isRTL ? 'القيمة' : 'Value'}</th><th>${isRTL ? 'المعيار' : 'Benchmark'}</th><th>${isRTL ? 'الرأي' : 'Verdict'}</th></tr></thead><tbody>
    ${ratios.map(r => `<tr class="${r.status}"><td>${isRTL ? r.categoryAr : r.category}</td><td>${isRTL ? r.ratioNameAr : r.ratioName}</td><td>${isRTL ? r.formulaAr : r.formula}</td><td><b>${r.displayValue}</b></td><td>${r.benchmark}</td><td>${r.status === 'Good' ? (isRTL ? 'آمن' : 'Safe') : r.status === 'Warning' ? (isRTL ? 'مراقبة' : 'Watch') : (isRTL ? 'خطر' : 'Risk')}</td></tr>`).join('')}
    </tbody></table>
    ${result.risks.length > 0 ? `<h3 style="color:#f44336;margin-top:15px">${isRTL ? 'المخاطر' : 'Risks'}</h3><ul>${result.risks.map(r => `<li><b>${isRTL ? r.riskNameAr : r.riskName}</b> (${r.severity}): ${isRTL ? r.descriptionAr : r.description}</li>`).join('')}</ul>` : ''}
    <div style="text-align:center;margin-top:15px;color:#999;font-size:10px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  const renderRatioSection = (ratios: FinancialRatio[], title: string, titleAr: string) => (
    <Paper sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          {categoryIcon(ratios[0]?.category || '')}
        </Box>
        <Typography fontWeight="bold" sx={{ flex: 1 }}>{isRTL ? titleAr : title}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {ratios.map(r => (
            <Box key={r.ratioId} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: statusColor(r.status) }} />
          ))}
        </Box>
      </Box>
      {ratios.map(ratio => (
        <Box key={ratio.ratioId}>
          <Box onClick={() => toggleExpand(ratio.ratioId)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, cursor: 'pointer', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)' } }}>
            {statusIcon(ratio.status)}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500}>{isRTL ? ratio.ratioNameAr : ratio.ratioName}</Typography>
              <Typography variant="caption" color="text.secondary">{isRTL ? ratio.formulaAr : ratio.formula}</Typography>
            </Box>
            <Box sx={{ textAlign: isRTL ? 'left' : 'right', minWidth: 120 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: statusColor(ratio.status), lineHeight: 1.2 }}>
                {ratio.displayValue}
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ color: statusColor(ratio.status) }}>
                {ratio.status === 'Good' ? (isRTL ? 'نتيجة آمنة' : 'Safe') : ratio.status === 'Warning' ? (isRTL ? 'يحتاج مراقبة' : 'Watch') : (isRTL ? 'خطر' : 'Risk')}
              </Typography>
            </Box>
            <Chip label={ratio.benchmark} size="small" variant="outlined" sx={{ minWidth: 70 }} />
            <IconButton size="small">{expanded[ratio.ratioId] ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
          <Collapse in={expanded[ratio.ratioId]}>
            <Box sx={{ px: 3, pb: 2, pt: 1, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
              {/* Formula with actual values */}
              {ratio.numerator != null && ratio.denominator != null && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: isDark ? 'rgba(102,126,234,0.08)' : 'rgba(102,126,234,0.05)', borderColor: isDark ? 'rgba(102,126,234,0.3)' : 'rgba(102,126,234,0.2)' }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ color: '#667eea', display: 'block', mb: 0.5 }}>
                    {isRTL ? 'المعادلة:' : 'Formula:'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {isRTL ? ratio.formulaAr : ratio.formula}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, color: isDark ? '#90caf9' : '#1565c0' }}>
                    = {fmt(ratio.numerator)} ÷ {fmt(ratio.denominator)} = <strong>{ratio.displayValue}</strong>
                  </Typography>
                </Paper>
              )}
              {/* Article */}
              <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                {isRTL ? ratio.articleAr : ratio.article}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      ))}
    </Paper>
  );

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 0.5 }}>
            {isRTL ? 'التحليل المالي الشامل' : 'Comprehensive Financial Analysis'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL ? 'نسب مالية + تقييم استمرارية + تحليل مخاطر — وفق IFRS و ISA 570' : 'Financial Ratios + Going Concern + Risk Assessment — per IFRS & ISA 570'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={isRTL ? 'من تاريخ' : 'From Date'}
              value={fromDate}
              onChange={(date) => setFromDate(date)}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={toDate || maxDate}
              slotProps={{
                textField: { size: 'small', sx: { minWidth: 220 } }
              }}
            />
            <DatePicker
              label={isRTL ? 'إلى تاريخ' : 'To Date'}
              value={toDate}
              onChange={(date) => setToDate(date)}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={fromDate || minDate}
              maxDate={maxDate}
              slotProps={{
                textField: { size: 'small', sx: { minWidth: 220 } }
              }}
            />
          </LocalizationProvider>
          <Button variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={runAnalysis} disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', px: 4, py: 1, borderRadius: 3, gap: 1 }}>
            {loading ? (isRTL ? 'جاري التحليل...' : 'Analyzing...') : (isRTL ? 'تحليل' : 'Analyze')}
          </Button>
          {result && (<>
            <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel} sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white', borderRadius: 3 }}>Excel</Button>
            <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf} sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white', borderRadius: 3 }}>PDF</Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white', borderRadius: 3 }}>{isRTL ? 'طباعة' : 'Print'}</Button>
          </>)}
        </Box>
      </Box>

      {result && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Card sx={{ background: overallGradient(result.overallStatus), color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'الوضع العام' : 'Overall Status'}</Typography>
                <Typography variant="h4" fontWeight="bold">{isRTL ? result.overallStatusAr : result.overallStatus}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'نسب سليمة' : 'Good Ratios'}</Typography>
                <Typography variant="h4" fontWeight="bold">{result.goodCount} / {result.totalRatios}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'تحذيرات' : 'Warnings'}</Typography>
                <Typography variant="h4" fontWeight="bold">{result.warningCount}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: result.dangerCount > 0 ? 'linear-gradient(135deg, #f44336 0%, #e57373 100%)' : 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'مخاطر' : 'Dangers'}</Typography>
                <Typography variant="h4" fontWeight="bold">{result.dangerCount}</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Z-Score Card */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, background: zScoreGradient(result.zScore.zone), color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Altman Z-Score — {isRTL ? 'مؤشر التنبؤ بالإفلاس' : 'Bankruptcy Prediction'}</Typography>
                <Typography variant="h3" fontWeight="bold">{result.zScore.zScore.toFixed(2)}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>{isRTL ? result.zScore.zoneAr : result.zScore.zone}</Typography>
              </Box>
              <Box sx={{ textAlign: isRTL ? 'left' : 'right', maxWidth: 400 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? result.zScore.interpretationAr : result.zScore.interpretation}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'X1 WC/Assets', value: result.zScore.x1_WorkingCapitalToAssets },
                { label: 'X2 RE/Assets', value: result.zScore.x2_RetainedEarningsToAssets },
                { label: 'X3 EBIT/Assets', value: result.zScore.x3_EBITToAssets },
                { label: 'X4 Equity/Debt', value: result.zScore.x4_EquityToDebt },
                { label: 'X5 Rev/Assets', value: result.zScore.x5_RevenueToAssets },
              ].map(x => (
                <Chip key={x.label} label={`${x.label}: ${x.value.toFixed(3)}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
              ))}
            </Box>
          </Paper>

          {/* Risks */}
          {result.risks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {result.risks.map((risk, i) => (
                <Alert key={i} severity={risk.severity === 'Critical' || risk.severity === 'High' ? 'error' : 'warning'} sx={{ mb: 1, borderRadius: 2 }}>
                  <Typography fontWeight="bold">{isRTL ? risk.riskNameAr : risk.riskName} — <Chip label={risk.severity} size="small" color={severityColor(risk.severity) as any} /></Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{isRTL ? risk.descriptionAr : risk.description}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>{isRTL ? risk.recommendationAr : risk.recommendation}</Typography>
                </Alert>
              ))}
            </Box>
          )}

          {/* Tabs */}
          <Paper sx={{ mb: 2, borderRadius: 3 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab label={isRTL ? 'السيولة' : 'Liquidity'} />
              <Tab label={isRTL ? 'الربحية' : 'Profitability'} />
              <Tab label={isRTL ? 'الملاءة' : 'Solvency'} />
              <Tab label={isRTL ? 'الكفاءة' : 'Efficiency'} />
              <Tab label={isRTL ? 'التدفقات النقدية' : 'Cash Flow'} />
              <Tab label={isRTL ? 'البيانات الخام' : 'Raw Data'} />
            </Tabs>
          </Paper>

          {/* Ratio Sections */}
          {tab === 0 && renderRatioSection(result.liquidityRatios, 'Liquidity Ratios', 'نسب السيولة')}
          {tab === 1 && renderRatioSection(result.profitabilityRatios, 'Profitability Ratios', 'نسب الربحية')}
          {tab === 2 && renderRatioSection(result.solvencyRatios, 'Solvency & Leverage Ratios', 'نسب الملاءة والرافعة المالية')}
          {tab === 3 && renderRatioSection(result.efficiencyRatios, 'Efficiency Ratios', 'نسب الكفاءة')}
          {tab === 4 && renderRatioSection(result.cashFlowRatios, 'Cash Flow Ratios', 'نسب التدفقات النقدية')}

          {/* Raw Data */}
          {tab === 5 && (
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'البند' : 'Item'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{isRTL ? 'القيمة (د.ك)' : 'Value (KWD)'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    [isRTL ? 'إجمالي الأصول' : 'Total Assets', result.totalAssets],
                    [isRTL ? 'الأصول المتداولة' : 'Current Assets', result.totalCurrentAssets],
                    [isRTL ? 'الأصول الثابتة' : 'Fixed Assets', result.totalFixedAssets],
                    [isRTL ? 'إجمالي الخصوم' : 'Total Liabilities', result.totalLiabilities],
                    [isRTL ? 'الخصوم المتداولة' : 'Current Liabilities', result.totalCurrentLiabilities],
                    [isRTL ? 'الخصوم طويلة الأجل' : 'Long-Term Liabilities', result.totalLongTermLiabilities],
                    [isRTL ? 'حقوق الملكية' : 'Total Equity', result.totalEquity],
                    [isRTL ? 'الأرباح المحتجزة' : 'Retained Earnings', result.retainedEarnings],
                    [isRTL ? 'الإيرادات' : 'Revenue', result.totalRevenue],
                    [isRTL ? 'تكلفة العمليات' : 'COGS', result.cogs],
                    [isRTL ? 'مجمل الربح' : 'Gross Profit', result.grossProfit],
                    [isRTL ? 'المصروفات التشغيلية' : 'Operating Expenses', result.operatingExpenses],
                    [isRTL ? 'الدخل التشغيلي' : 'Operating Income', result.operatingIncome],
                    [isRTL ? 'صافي الدخل' : 'Net Income', result.netIncome],
                    [isRTL ? 'تدفق تشغيلي' : 'Cash from Operations', result.cashFromOperations],
                    [isRTL ? 'تدفق استثماري' : 'Cash from Investing', result.cashFromInvesting],
                    [isRTL ? 'تدفق تمويلي' : 'Cash from Financing', result.cashFromFinancing],
                  ].map(([label, value], i) => (
                    <TableRow key={i}>
                      <TableCell>{label as string}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: (value as number) < 0 ? 'error.main' : 'inherit' }}>
                        {fmt(value as number)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {!result && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <Assessment sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {isRTL ? 'اضغط "تحليل" لبدء التحليل المالي الشامل' : 'Click "Analyze" to run comprehensive financial analysis'}
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            {isRTL ? '35+ نسبة مالية + Altman Z-Score + تقييم مخاطر' : '35+ financial ratios + Altman Z-Score + risk assessment'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default FinancialAnalysisReport;
